import React, { useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';
import { Menu } from '@mui/material';
import {
  DropdownHeader,
  SearchInput,
  DocumentsList,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  DropdownFooter,
  DocumentCount
} from './DocumentsDropdown.styled';
import DocumentListItem from './DocumentListItem';
import DescriptionIcon from '@mui/icons-material/Description';
import useStore from '../../hooks/useStore';

/**
 * Documents Dropdown Component
 * Shows list of all chat documents with search and filter
 * Globally accessible from any module with chat
 */
const DocumentsDropdown = observer(({
  anchorEl,
  open,
  onClose,
  onSelectDocument
}) => {
  const { chatDocumentsStore } = useStore();
  const [searchQuery, setSearchQuery] = useState('');

  // Filter documents based on search
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) {
      return chatDocumentsStore.sortedDocuments;
    }
    return chatDocumentsStore.searchDocuments(searchQuery);
  }, [searchQuery, chatDocumentsStore.sortedDocuments, chatDocumentsStore]);

  const handleSelectDocument = useCallback((document) => {
    onSelectDocument?.(document);
    onClose?.();
    setSearchQuery(''); // Reset search on selection
  }, [onSelectDocument, onClose]);

  const handleDeleteDocument = useCallback((documentId) => {
    chatDocumentsStore.deleteDocument(documentId);
  }, [chatDocumentsStore]);

  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleClose = useCallback(() => {
    setSearchQuery(''); // Reset search on close
    onClose?.();
  }, [onClose]);

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      PaperProps={{
        style: {
          maxHeight: '500px',
          width: '400px',
          borderRadius: '12px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }
      }}
    >
      <DropdownHeader>
        <SearchInput
          type="text"
          placeholder="Search documents..."
          value={searchQuery}
          onChange={handleSearchChange}
          onClick={(e) => e.stopPropagation()}
        />
      </DropdownHeader>

      <DocumentsList>
        {filteredDocuments.length === 0 ? (
          <EmptyState>
            <EmptyStateIcon>
              <DescriptionIcon style={{ fontSize: '48px', color: '#D1D5DB' }} />
            </EmptyStateIcon>
            <EmptyStateText>
              {searchQuery ? 'No documents found' : 'No documents yet'}
            </EmptyStateText>
            {searchQuery && (
              <EmptyStateText style={{ fontSize: '13px', marginTop: '4px' }}>
                Try a different search term
              </EmptyStateText>
            )}
          </EmptyState>
        ) : (
          filteredDocuments.map((doc) => (
            <DocumentListItem
              key={doc.id}
              document={doc}
              onSelect={handleSelectDocument}
              onDelete={handleDeleteDocument}
            />
          ))
        )}
      </DocumentsList>

      {filteredDocuments.length > 0 && (
        <DropdownFooter>
          <DocumentCount>
            {filteredDocuments.length} document{filteredDocuments.length !== 1 ? 's' : ''}
            {searchQuery && ` found`}
          </DocumentCount>
        </DropdownFooter>
      )}
    </Menu>
  );
});

export default DocumentsDropdown;

